import React from "react";
import { Form, Input, Select, Label } from "semantic-ui-react";
import { countryOptionsWithCurrent } from "../../../../utils/countries";
import "./Dashboard.scss";

const fmt0 = (v) => (isFinite(v) ? Math.round(v).toLocaleString("es-AR") : "—");
const fmt = (v, d = 1) => (isFinite(v) ? Number(v).toLocaleString("es-AR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—");

const ESTADO_LABEL = {
  preparacion: "Preparación",
  comprometida: "Comprometida",
  presentada: "Presentada",
  revision: "En revisión",
  aprobada: "Aprobada",
  anunciada: "Anunciada",
};

// Portado de vDash() del prototipo (línea 444). Las 6 etapas oficiales
// (Register · Commit · Develop · Submit · Communicate · Disclose) y a
// qué paso del módulo llevan al hacer clic, igual que el original.
const ETAPAS = [
  { t: "Registro", d: "Alta en el SBTi Services Validation Portal", goto: "p6" },
  { t: "Compromiso", d: "Opcional (corporativa): abre ventana de 24 meses. PYME va directo a presentar.", goto: "p6" },
  { t: "Desarrollo", d: "Objetivos según Criterios V5.3.1 + guía sectorial Indumentaria y Calzado", goto: "p4" },
  { t: "Presentación", d: "Formulario en el portal + pago de tarifa de validación", goto: "p6" },
  { t: "Comunicación", d: "Anuncio en el Target Dashboard ≤ 6 meses desde la aprobación", goto: "p6" },
  { t: "Divulgación", d: "Progreso público anual + revisión obligatoria a los 5 años", goto: "p7" },
];

export function Dashboard({ record, computed, patchSection, onNavigate }) {
  const meta = record.meta || {};
  const params = computed?.params || {};
  const calc = computed?.calc || {};
  const validation = record.validation || {};
  const currentPhase = computed?.currentPhase ?? 0;
  const dueDates = computed?.dueDates || [];

  const setMeta = (patch) => patchSection("meta", { ...meta, ...patch });

  // Mismo subconjunto que el prototipo: compromiso, anuncio (dueDates ya
  // lo omite si announcedOn está cargado) y disparo quinquenal.
  const hitos = dueDates.filter((d) =>
    ["Fin de la ventana de compromiso para presentar", "Límite para anunciar en el Target Dashboard", "Disparo de la revisión quinquenal obligatoria"].includes(d.label)
  );

  return (
    <div className="sbti-dash">
      <div className="sbti-pagehead">
        <div className="sbti-crumb">Sustemia · Módulo Ambiental · SBTi</div>
        <h2>Hoja de ruta <span className="sbti-pgram"><span>SBTi</span></span> — de la evidencia al objetivo validado</h2>
        <p className="sbti-sub">
          Proceso oficial en 6 etapas (SBTi Services) aplicado al sector Indumentaria y Calzado. El inventario del
          Paso 1 puede cargarse a mano o sincronizarse desde el módulo GEI de Sustemia.
        </p>
      </div>

      <div className="sbti-kpis">
        <div className="sbti-kpi sbti-kpi--alert"><div className="sbti-kpi__l">Empresa</div><div className="sbti-kpi__v" style={{ fontSize: 16 }}>{meta.companyName || "—"}</div></div>
        <div className="sbti-kpi"><div className="sbti-kpi__l">Año base</div><div className="sbti-kpi__v">{record.inventory?.baseYear || "—"}</div></div>
        <div className="sbti-kpi"><div className="sbti-kpi__l">Emisiones año base</div><div className="sbti-kpi__v">{calc.totalWithIndirect ? fmt0(calc.totalWithIndirect) : "—"} <span>tCO₂e</span></div></div>
        <div className="sbti-kpi"><div className="sbti-kpi__l">Alcance 3 (elegible)</div><div className="sbti-kpi__v">{calc.scope3Eligible ? fmt(calc.scope3Percent) : "—"} <span>% del total</span></div></div>
        <div className="sbti-kpi"><div className="sbti-kpi__l">Ruta</div><div className="sbti-kpi__v" style={{ fontSize: 16 }}>{record.route?.type === "sme" ? "PYME" : record.route?.type === "corp" ? "Corporativa" : "—"}</div></div>
        <div className="sbti-kpi"><div className="sbti-kpi__l">Estado de validación</div><div className="sbti-kpi__v" style={{ fontSize: 16 }}>{ESTADO_LABEL[validation.status] || "Preparación"}</div></div>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">PROCESO OFICIAL</span> Las 6 etapas del ciclo SBTi</h3>
        <div className="sbti-road">
          {ETAPAS.map((p, i) => (
            <div className="sbti-road__step" key={i} onClick={() => onNavigate(p.goto)}>
              <div className={`sbti-road__box ${i < currentPhase ? "done" : i === currentPhase ? "now" : ""}`}>
                <div className="sbti-road__num">ETAPA {i + 1}</div>
                <div className="sbti-road__tt">{p.t}</div>
              </div>
              <div className="sbti-road__label">{p.d}</div>
            </div>
          ))}
        </div>
        <div className="sbti-src">
          <b>Fuente:</b> SBTi Services — «Set a science-based target today» (Register · Commit · Develop · Submit ·
          Communicate · Disclose). El compromiso es opcional; las PYME no son elegibles para comprometerse y
          presentan directo. Verificado {params.verificado || "—"}.
        </div>
      </div>

      <div className="sbti-dash__grid2">
        <div className="sbti-card">
          <h3>Próximos hitos con fecha</h3>
          {hitos.length ? (
            <table className="sbti-plain-table">
              <thead><tr><th>Hito</th><th>Fecha límite</th></tr></thead>
              <tbody>
                {hitos.map((h) => (
                  <tr key={h.label}><td>{h.label}</td><td className="num">{new Date(h.date + "T00:00:00").toLocaleDateString("es-AR")}</td></tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="sbti-hint">
              Todavía no hay fechas cargadas. Registrá las fechas del proceso en el Paso 6 y acá aparecen los
              vencimientos calculados automáticamente.
            </p>
          )}
        </div>
        <div className="sbti-card">
          <h3>Marco vigente aplicado por este módulo</h3>
          <div className="sbti-chips">
            {params.criterios_version && <Label className="sbti-chip">{params.criterios_version}</Label>}
            {params.netzero_version && <Label className="sbti-chip">{params.netzero_version}</Label>}
            <Label className="sbti-chip">Guía sectorial Apparel &amp; Footwear (SBTi/WRI, 2019)</Label>
            <Label className="sbti-chip">Guía FLAG (act. mar-2026)</Label>
            <Label className="sbti-chip">Guía de revisión quinquenal (dic-2025)</Label>
          </div>
          {params.v2_nota && <div className="sbti-note" style={{ marginTop: 12 }}><b>Atención:</b> {params.v2_nota}</div>}
        </div>
      </div>

      <div className="sbti-card">
        <h3>Datos de la empresa</h3>
        <Form>
          <Form.Group widths={4}>
            <Form.Field>
              <label>Empresa (nivel matriz/grupo)</label>
              <Input value={meta.companyName || ""} onChange={(_, d) => setMeta({ companyName: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>País sede</label>
              <Select
                search
                selection
                allowAdditions
                options={countryOptionsWithCurrent(meta.country)}
                value={meta.country || ""}
                placeholder="País"
                noResultsMessage="Escribí para agregarlo"
                onAddItem={(_, d) => setMeta({ country: d.value })}
                onChange={(_, d) => setMeta({ country: d.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>Sector</label>
              <Input value={meta.sector || ""} onChange={(_, d) => setMeta({ sector: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Responsable</label>
              <Input value={meta.owner || ""} onChange={(_, d) => setMeta({ owner: d.value })} />
            </Form.Field>
          </Form.Group>
        </Form>
        <p className="sbti-hint">
          El objetivo se presenta a nivel <b>matriz o grupo</b>, no por subsidiaria (Criterio NT C1). Si gestionás
          varias plantas en Sustemia, el inventario del Paso 1 permite consolidarlas.
        </p>
      </div>
    </div>
  );
}
